import { observable, action } from "mobx";
import { findIndex } from "lodash";
import { store } from "@store";
import { ConsultantEditorStore } from "../../ConsultantEditorStore";
import {
  ExpertiseProfession,
  ExpertiseProfessionStore
} from "../ExpertiseProfessionStore";

export interface SSKProfession extends ExpertiseProfession {
  areasOfExpertise: AreaOfExpertise[];
  specializations: Specialization[];
}

export interface AreaOfExpertise {
  name: string;
  yearFrom: string;
  interested: boolean;
  comment: string;
  qualified: boolean;
}

export interface Specialization {
  name: string;
  yearFrom: string;
  interested: boolean;
  comment: string;
  qualified: boolean;
}

export class SSKStore extends ExpertiseProfessionStore<SSKProfession> {
  @observable selectedAoE: string[];
  @observable selectedSpecialization: string[];

  constructor(
    profession: string,
    storeIndex: number,
    public editorStore: ConsultantEditorStore,
    public defaultAOE: string
  ) {
    super(profession, storeIndex, editorStore);
    this.selectedAoE = null;
    this.selectedSpecialization = null;
  }

  get areasOfExpertise(): AreaOfExpertise[] {
    return this.currentExpertiseProfession?.areasOfExpertise ?? [];
  }

  get specializations(): Specialization[] {
    return this.currentExpertiseProfession?.specializations ?? [];
  }

  get selectAoEList() {
    return this.editorStore
      .getAOEsOnProfession(this.profession)
      .filter((x) => x !== this.defaultAOE)
      .filter((x) => !this.areasOfExpertise.any((y) => y.name === x));
  }

  get selectSpecializationList() {
    return store()
      .cache.getAreaOfExpOnProfessions([this.profession])
      .flatMap((x) => x.Specializations)
      .unique()
      .filter((x) => !this.specializations.any((y) => y.name === x));
  }

  @action setSelectedAoE = (val: string[]) => {
    this.selectedAoE = val;
  };

  @action setSelectedSpecialization = (val: string[]) => {
    this.selectedSpecialization = val;
  };

  @action addAoE = () => {
    this.selectedAoE.map((x) => {
      const i = findIndex(this.areasOfExpertise, (a) => a.name === x);
      if (i === -1) {
        this.editorStore.form.push(`professions.${this.storeIndex}.areasOfExpertise`, {
          name: x,
          yearFrom: "",
          interested: true,
          qualified: true,
          comment: ""
        });
      }
    });
    this.setSelectedAoE(null);
  };

  addSpec = () => {
    this.selectedSpecialization.map((x) => {
      const i = findIndex(this.specializations, (a) => a.name === x);
      if (i === -1) {
        super.addSpec<Specialization>({
          name: x,
          yearFrom: "",
          interested: true,
          qualified: true,
          comment: ""
        });
      }
    });
    this.setSelectedSpecialization(null);
  };

  @action removeSpec = (path: string, i: number) => {
    this.editorStore.form.removeByIndex(path, i);
  };

  initForm() {
    if (!this.currentExpertiseProfession) {
      this.editorStore.form.setValue(`professions.${this.storeIndex}`, {
        professionName: this.profession,
        areasOfExpertise: this.defaultAOE
          ? [
              {
                name: this.defaultAOE,
                interested: true,
                qualified: true,
                comment: "",
                yearFrom: ""
              }
            ]
          : [],
        specializations: []
      });
    }
  }
}
